import React, { useEffect, useState } from "react";
import {
  BarChart3,
  ChevronDown,
  FileText,
  Landmark,
  LayoutDashboard,
  Menu,
  Package,
  Plus,
  ReceiptText,
  Settings,
  ShieldCheck,
  ShoppingBag,
  ShoppingCart,
  UserRound,
  Users,
  X,
} from "lucide-react";

const menuItems = [
  { id: "dashboard", label: "Dashboard", icon: LayoutDashboard, route: "dashboard" },
  { id: "items", label: "Items", icon: Package, route: "items", createRoute: "ItemsNew" },
  {
    id: "banking",
    label: "Banking",
    icon: Landmark,
    children: [
      { label: "Bank Accounts", route: "bankacc", createRoute: "BankAccNew", matches: ["bankacc", "BankAccNew"] },
    ],
  },
  {
    id: "sales",
    label: "Sales",
    icon: ShoppingCart,
    children: [
      { label: "Customers", route: "Customers", createRoute: "CustomerNew", matches: ["Customers", "CustomerNew"] },
      { label: "Estimates", route: "Estimates", createRoute: "EstimatesNewButton", matches: ["Estimates", "EstimatesNewButton"] },
      { label: "Sales Orders", route: "Order", createRoute: "SalesOrderNewButton", matches: ["Order", "SalesOrderNewButton"] },
      { label: "Delivery Challans", route: "Challans", createRoute: "DeliveryChallansNew", matches: ["Challans", "DeliveryChallansNew"] },
      { label: "Invoices", route: "Invoices", createRoute: "InvoicesNew", matches: ["Invoices", "InvoicesNew"] },
      { label: "Pro Forma", route: "Proforma", createRoute: "ProformaNew", matches: ["Proforma", "ProformaNew"] },
      { label: "Recurring Invoices", route: "RecurringInvoices", createRoute: "RecurringInvoiceNew", matches: ["RecurringInvoices", "RecurringInvoiceNew"] },
      { label: "Payment Received", route: "Payment", createRoute: "PaymentRecivedNew", matches: ["Payment", "PaymentRecivedNew"] },
      { label: "Credit Notes", route: "Credit", createRoute: "CreditNoteNew", matches: ["Credit", "CreditNoteNew"] },
      { label: "Quotations", route: "quotes", createRoute: "quotesnew", matches: ["quotes", "quotesnew"] },
    ],
  },
  {
    id: "purchase",
    label: "Purchase",
    icon: ShoppingBag,
    children: [
      { label: "Vendors", route: "vendors", createRoute: "VendorsNew", matches: ["vendors", "VendorsNew"] },
      { label: "Expenses", route: "expenses", createRoute: "ExpensesNew", matches: ["expenses", "ExpensesNew"] },
      { label: "Bills", route: "bills", createRoute: "BillsNew", matches: ["bills", "BillsNew"] },
      { label: "Purchase Orders", route: "purchaseOrder", createRoute: "PurchaseOrderNew", matches: ["purchaseOrder", "PurchaseOrderNew"] },
      { label: "Payments Made", route: "paymentsMade", createRoute: "PaymentsMadeNew", matches: ["paymentsMade", "PaymentsMadeNew"] },
    ],
  },
  { id: "expenses", label: "Expenses", icon: ReceiptText, route: "expenses", createRoute: "ExpensesNew" },
  { id: "contacts", label: "Contacts", icon: Users, route: "Customers", createRoute: "CustomerNew" },
  {
    id: "users",
    label: "Users & Roles",
    icon: ShieldCheck,
    children: [{ label: "Users", route: "Users", matches: ["Users"] }],
  },
  { id: "salesperson", label: "Manage SalesPerson", icon: UserRound, route: "SalesPerson" },
  { id: "gst", label: "GST Filing", icon: FileText, route: "GstFilling" },
  {
    id: "reports",
    label: "Reports",
    icon: BarChart3,
    children: [
      { label: "Reports Center", route: "reports", matches: ["reports"] },
      { label: "Profit & Loss", route: "ProfitAndLoss", matches: ["ProfitAndLoss", "ProfitAndLossShedual3", "HorizontalProfit"] },
      { label: "Balance Sheet", route: "BalanceSheet", matches: ["BalanceSheet", "HorizontalBalanceSheet", "BalanceSheetSchedule"] },
      { label: "Receivables", route: "ReceivableSummary", matches: ["ReceivableSummary", "ReceivableDetails", "AgingSummary"] },
      { label: "Payables", route: "PayableSummary", matches: ["PayableSummary", "PayableDetails", "ApAgingSummary"] },
      { label: "Sales Reports", route: "SalesCustomer", matches: ["SalesCustomer", "SalesItems", "SalesByInvoice", "SalesOrderDetails"] },
      { label: "Cash Statement", route: "CashStatement", matches: ["CashStatement"] },
      { label: "Item Stocks", route: "ItemStocks", matches: ["ItemStocks"] },
    ],
  },
  { id: "settings", label: "Settings", icon: Settings, route: "Setting" },
];

const itemMatchesRoute = (item, activeKey) => {
  if (item.matches?.includes(activeKey)) return true;
  if (item.route === activeKey || item.createRoute === activeKey || item.id === activeKey) return true;
  return item.children?.some((child) => itemMatchesRoute(child, activeKey));
};

const ModernSidebar = ({ navigation, activeKey }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [openGroups, setOpenGroups] = useState({});

  useEffect(() => {
    const activeGroup = menuItems.find((item) =>
      item.children?.some((child) => itemMatchesRoute(child, activeKey))
    );

    if (activeGroup) {
      setOpenGroups((current) =>
        Object.prototype.hasOwnProperty.call(current, activeGroup.id)
          ? current
          : { ...current, [activeGroup.id]: true }
      );
    }
  }, [activeKey]);

  const navigateTo = (route) => {
    navigation.navigate(route);
    setIsOpen(false);
  };

  const toggleGroup = (id) => {
    setOpenGroups((current) => ({ ...current, [id]: !Boolean(current[id]) }));
  };

  return (
    <>
      <button className="zarp-mobile-menu" onClick={() => setIsOpen(true)} aria-label="Open menu">
        <Menu size={20} />
      </button>

      <aside className={`zarp-sidebar ${isOpen ? "is-open" : ""}`}>
        <div className="zarp-sidebar-head">
          <button className="zarp-sidebar-close" onClick={() => setIsOpen(false)} aria-label="Close menu">
            <X size={18} />
          </button>
          <button className="zarp-logo-lockup" onClick={() => navigateTo("dashboard")}>
            <span className="zarp-logo-mark">Z</span>
            <span className="zarp-logo-text">ZARP</span>
          </button>
        </div>

        <nav className="zarp-sidebar-nav">
          {menuItems.map((item) => {
            const Icon = item.icon;
            const isActive = itemMatchesRoute(item, activeKey);
            const isExpanded = Boolean(openGroups[item.id]);

            if (item.children) {
              return (
                <div className={`zarp-nav-group ${isExpanded ? "is-expanded" : ""}`} key={item.id}>
                  <button
                    className={`zarp-nav-item ${isActive ? "is-active" : ""}`}
                    onClick={() => toggleGroup(item.id)}
                  >
                    <span className="zarp-nav-icon">
                      <Icon size={18} strokeWidth={2.1} />
                    </span>
                    <span title={item.label}>{item.label}</span>
                    <ChevronDown className="zarp-nav-chevron" size={15} />
                  </button>

                  <div className="zarp-subnav" aria-hidden={!isExpanded}>
                    <div className="zarp-subnav-inner">
                      {item.children.map((child) => {
                          const childActive = itemMatchesRoute(child, activeKey);
                          return (
                            <div className={`zarp-subnav-row ${childActive ? "is-active" : ""}`} key={child.route}>
                              <button className="zarp-subnav-link" title={child.label} onClick={() => navigateTo(child.route)}>
                                {child.label}
                              </button>
                              {child.createRoute && (
                                <button
                                  className="zarp-subnav-add"
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    navigateTo(child.createRoute);
                                  }}
                                  aria-label={`Create ${child.label}`}
                                >
                                  <Plus size={14} />
                                </button>
                              )}
                            </div>
                          );
                        })}
                    </div>
                  </div>
                </div>
              );
            }

            return (
              <div className="zarp-nav-single" key={item.id}>
                <button
                  className={`zarp-nav-item ${isActive ? "is-active" : ""}`}
                  onClick={() => navigateTo(item.route)}
                >
                  <span className="zarp-nav-icon">
                    <Icon size={18} strokeWidth={2.1} />
                  </span>
                  <span title={item.label}>{item.label}</span>
                </button>
                {item.createRoute && (
                  <button className="zarp-nav-add" onClick={() => navigateTo(item.createRoute)} aria-label={`Create ${item.label}`}>
                    <Plus size={14} />
                  </button>
                )}
              </div>
            );
          })}
        </nav>

      </aside>

      {isOpen && <button className="zarp-sidebar-overlay" onClick={() => setIsOpen(false)} aria-label="Close menu" />}
    </>
  );
};

export default ModernSidebar;
